import { Fragment, type ReactElement, useContext, useState } from "react"

import { useQuery } from "react-query"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Skeleton } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../../services/KonanAPI"
import { FeedbackInsightList } from "../../../../types/generated/api/FeedbackInsightList"
import { InsightList } from "../../../../types/generated/api/InsightList"
import { LabelReasonsInsight } from "../../../../types/generated/api/LabelReasonsInsight"
import { ReportContext } from "../../Context"
import { setLabelsCriteria } from "../../Slice"
import type { LabelsCriteriaInsightProps } from "../../interfaces"
import { ChartContainer } from "./ChartContainer"

/**
 * Renders a LabelReasonFeedbackInsight component
 *
 * Fetches insight data from the API and displays it using the ChartContainer component.
 *
 * @param {InsightList | FeedbackInsightList} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {ReactElement} A Grid item containing a ReasonsBreakdownChart
 */
export function LabelReasonFeedbackInsight(props: { insight: InsightList | FeedbackInsightList }): ReactElement {
  const { insight } = props

  const { id: projectId } = useParams<{ id: string }>()
  const { report } = useContext(ReportContext)
  const dispatch = useDispatch()

  const [isChartFullWidth, setIsChartFullWidth] = useState(false)

  // Fetch label-reasons insight data from the API
  const { data: labelReasonInsight, isLoading: isLoadingLabelReasonInsights } = useQuery<
    AxiosResponse<LabelReasonsInsight>,
    AxiosError,
    { version: "V1"; data: Record<string, Record<string, Record<string, number>>> } | undefined
  >(
    ["label-criteria", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      select(response) {
        if (response.data.version === "V1") {
          return {
            version: response.data.version,
            data: response.data.data as unknown as Record<string, Record<string, Record<string, number>>>,
          }
        }

        return undefined
      },
      onSuccess: (data) => {
        if (data && data.version === "V1") {
          dispatch(setLabelsCriteria(data as LabelsCriteriaInsightProps))
        }
      },
      // Only enable the query when all required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const initialLabel = Object.keys(labelReasonInsight?.data ?? {})[0]

  const initialReasonsForFirstLabel = Object.keys(labelReasonInsight?.data?.[initialLabel as string] ?? {}).map(
    (reason) => ({
      id: `${initialLabel}-${reason}`,
      value: reason,
      label: reason,
    }),
  )

  const initialFeedbacksForFirstReasonsForFirstLabel = initialReasonsForFirstLabel?.flatMap((reason) =>
    Object.entries(labelReasonInsight?.data?.[initialLabel as string]?.[reason.value] ?? {}).map(
      ([feedback, value]) => ({
        reason: reason.value,
        feedback: {
          id: `${initialLabel}-${reason.value}-${feedback}`,
          label: feedback,
          value: value,
        },
      }),
    ),
  )

  return (
    <Grid container display={"flex"} xs={12} rowGap={2}>
      {!isLoadingLabelReasonInsights ? (
        <Fragment>
          <ChartContainer
            insight={insight}
            LabelReasonFeedbackInsightData={labelReasonInsight?.data}
            isLoading={isLoadingLabelReasonInsights}
            isChartFullWidth={isChartFullWidth}
            setIsChartFullWidth={setIsChartFullWidth}
            initialSelectedLabel={initialLabel}
            initialSelectedReasons={initialReasonsForFirstLabel}
            initialSelectedFeedbacks={initialFeedbacksForFirstReasonsForFirstLabel}
          />

          <ChartContainer
            insight={insight}
            LabelReasonFeedbackInsightData={labelReasonInsight?.data}
            isLoading={isLoadingLabelReasonInsights}
            isChartFullWidth={isChartFullWidth}
            setIsChartFullWidth={setIsChartFullWidth}
            initialSelectedLabel={initialLabel}
            initialSelectedReasons={initialReasonsForFirstLabel}
            initialSelectedFeedbacks={initialFeedbacksForFirstReasonsForFirstLabel}
          />
        </Fragment>
      ) : (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="flex-start"
          p={2}
          marginInlineStart={2}
          border={"1px solid var(--base-border-1)"}
          borderRadius={2}
          bgcolor={"var(--base-background-2)"}
          height={"414px"}
        >
          <Skeleton width="100%" height={40} marginBottom={8} />
          <Skeleton width="75%" marginBottom={8} />
          <Skeleton width="60%" height={30} marginBottom={8} />
          <Skeleton width="30%" />
        </Grid>
      )}
    </Grid>
  )
}
